import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package, AlertTriangle, X, Truck, Clock, Check, Loader2, User, XCircle, ArrowDownUp,
  MessageCircle, ChevronDown, ChevronRight
} from 'lucide-react';
import { api } from '../../../api/client';
import { EntityLink } from '../../../components/shared/EntityLink';
import { hiRes, BatchData, ScanResult } from '../shared';
import { StatusBadge } from '../components/StatusBadge';
import { ConvertToInternalModal } from '../../../components/stock/ConvertToInternalModal';
import { LeftoverSearchModal } from './LeftoverSearchModal';
import { PendingSalesModal } from './PendingSalesModal';
import { Can, useCan } from '../../../lib/rbac';
import { CourierBand } from '../components/CourierBand';
import { CountryFlagWatermark } from '../components/CountryFlagWatermark';
import { MessagePanel } from '../../orders/MessagePanel';
import { AmazonOrdersStrip } from '../components/AmazonOrdersStrip';
import { useAuthStore } from '../../../stores/authStore';

export function ScanResultModal({ result, sellerId, batches, noBagReason, onClose, onItemAdded, onRequestLinkTracking, onRescan }: {
  result: ScanResult; sellerId: number; batches: BatchData[];
  noBagReason?: string | null;
  onClose: () => void; onItemAdded: () => void;
  onRequestLinkTracking?: (code: string) => void;
  onRescan?: (code: string) => void;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const canOperate = can('warehouse.operate');
  const canMoney = can('global.view_money');
  const [closeScanCode, setCloseScanCode] = useState((result as any).prefill_close_code || '');
  const [closeScanError, setCloseScanError] = useState('');
  const [closeScanLoading, setCloseScanLoading] = useState(false);
  // 2026-09-10 — depósito con dos correos: correo elegido a mano (envío sin correo definido) y
  // bolsa abierta desde la franja del popup (sin salir del flujo).
  const [chosenCourierKey, setChosenCourierKey] = useState<string | null>(null);
  const [bagOverride, setBagOverride] = useState<{ id: number; bag_code: string } | null>(null);
  const effectiveTargetBag = bagOverride ?? result.target_bag ?? null;
  const effectiveBagGap = bagOverride ? null : chosenCourierKey && result.bag_gap === 'COURIER_REQUIRED' ? null : result.bag_gap ?? null;
  const courierKeyBody = chosenCourierKey ? { courier_key: chosenCourierKey } : {};
  const [claimConfirmed, setClaimConfirmed] = useState(false);
  const [orderItems, setOrderItems] = useState<any[]>([]);
  const [leftoverBusy, setLeftoverBusy] = useState(false);
  const [convertModal, setConvertModal] = useState<{
    stockProductId: number;
    asin: string;
    title: string;
    unitCost: number;
    qtyOnHand: number;
    // Rescate de orden cancelada (escaneo bloqueado): source amazon_purchase +
    // seller de la orden (no el del terminal) + nota de rescate.
    source?: { type: string; purchase_id?: number; ml_order_id?: string };
    defaultNotes?: string;
    sellerId?: number;
    // Cantidad de la compra Amazon (rescates): sin esto el modal convertía
    // SIEMPRE 1 unidad aunque la compra fuera de 2+. Sobrantes siguen de a 1.
    qty?: number;
  } | null>(null);
  const [searchModalAsin, setSearchModalAsin] = useState<string | null>(null);
  const [pendingSaleFor, setPendingSaleFor] = useState<{ asin: string; amazonOrderId: string; title?: string | null } | null>(null);
  const [fullyReceived, setFullyReceived] = useState(false);
  // Bloque de mensajes con el comprador: arranca plegado en cada escaneo (ver más abajo).
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const closeRef = useRef<HTMLInputElement>(null);
  // Print job state (Task 14): badge + polling + manual reprint
  const [printJob, setPrintJob] = useState<ScanResult['print_job'] | null>(result.print_job || null);
  const [printStatus, setPrintStatus] = useState<string | null>(printJob?.status || null);
  const [printingPolling, setPrintingPolling] = useState<boolean>(
    printJob ? !['completed', 'failed'].includes(printJob.status) : false
  );
  // Voltaje 110 V: el PDF salió sin la hoja del aviso (falló el render).
  const [voltageMissing, setVoltageMissing] = useState<boolean>(!!result.voltage_label_missing);
  const [voltageBusy, setVoltageBusy] = useState(false);

  useEffect(() => {
    if (!printJob || !printingPolling) return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get<{ status: string }>(`/labels/print-jobs/${printJob.id}`);
        const newStatus = (r.data as any)?.status;
        if (newStatus) {
          setPrintStatus(newStatus);
          if (['completed', 'failed'].includes(newStatus)) {
            setPrintingPolling(false);
            clearInterval(iv);
          }
        }
      } catch {}
    }, 1000);
    return () => clearInterval(iv);
  }, [printJob, printingPolling]);

  // Tras reimprimir, devolver el foco al input de cierre para que el operador
  // pueda escanear la etiqueta ML sin tocar la pantalla (mismo patrón que el
  // auto-focus on mount)
  const refocusCloseScan = () => {
    setTimeout(() => {
      closeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      closeRef.current?.focus();
    }, 200);
  };

  const handleReprint = async () => {
    if (!result.order_label_id) {
      alert('No hay etiqueta de merge para esta orden');
      refocusCloseScan();
      return;
    }
    try {
      const r = await api.post<{ id: number; status: string; was_existing: boolean }>(
        '/labels/print-jobs',
        {
          seller_id: result.seller?.id,
          order_label_id: result.order_label_id,
          target_agent_id: localStorage.getItem('cbt_terminal_id'),
          force: true,
        }
      );
      if (r.success) {
        const job = r.data as any;
        setPrintJob({ id: job.id, status: job.status, was_existing: false });
        setPrintStatus(job.status);
        setPrintingPolling(true);
      } else {
        alert('Error al reimprimir: ' + (r.error || 'unknown'));
      }
    } catch (e: any) {
      alert('Error al reimprimir: ' + e.message);
    }
    refocusCloseScan();
  };

  /**
   * Voltaje 110 V: el PDF guardado NO tiene la hoja del aviso, así que
   * reimprimir el mismo archivo no alcanza. Primero se regenera la etiqueta
   * (vuelve a armar las 3 hojas y apaga la marca en la base) y recién después
   * se manda a imprimir.
   */
  const handleVoltageRelabel = async () => {
    const shipmentId = result.shipment?.ml_shipment_id;
    const sellerId = result.seller?.id;
    if (!shipmentId || !sellerId) {
      alert('No se puede regenerar: esta venta todavía no tiene envío de Mercado Libre');
      refocusCloseScan();
      return;
    }
    setVoltageBusy(true);
    try {
      const r = await api.post<{ results?: Array<{ success?: boolean; error?: string }> }>(
        '/labels/generate',
        { seller_id: sellerId, shipment_id: shipmentId, force: true }
      );
      const first = (r.data as any)?.results?.[0];
      if (r.success && first?.success) {
        setVoltageMissing(false);
        setVoltageBusy(false);
        await handleReprint();
        return;
      }
      alert('No se pudo regenerar la etiqueta 110 V: ' + (first?.error || r.error || 'unknown'));
    } catch (e: any) {
      alert('No se pudo regenerar la etiqueta 110 V: ' + e.message);
    }
    setVoltageBusy(false);
    refocusCloseScan();
  };

  const renderPrintBadge = () => {
    const printJobError = (result as any).print_job_error as string | undefined;
    if (!printJob && printJobError) {
      return (
        <div className="bg-red-100 border-l-4 border-red-500 p-3 mb-3 rounded">
          <strong>✗ No se pudo imprimir</strong>
          <div className="text-sm mt-1">{printJobError}</div>
          <div className="text-xs mt-1 text-gray-600">
            La etiqueta ML aún no está lista. Reintentá en unos minutos con el botón de reimprimir.
          </div>
        </div>
      );
    }
    if (!printJob) return null;
    const wasExisting = printJob.was_existing;
    const isCompleted = printStatus === 'completed';
    const isInProgress = ['queued', 'sent', 'printing'].includes(printStatus || '');
    const isFailed = printStatus === 'failed';

    if (wasExisting && isCompleted) {
      return (
        <div className="bg-green-100 border-l-4 border-green-500 p-3 mb-3 rounded">
          <strong>✓ Etiqueta ya impresa</strong>
          {printJob.agent_id && <span className="text-sm ml-2">en {printJob.agent_id}</span>}
        </div>
      );
    }
    if (wasExisting && isInProgress) {
      return (
        <div className="bg-blue-100 border-l-4 border-blue-500 p-3 mb-3 rounded">
          🖨 Etiqueta ya en cola desde otro scan, esperá...
        </div>
      );
    }
    if (isCompleted) {
      return (
        <div className="bg-green-100 border-l-4 border-green-500 p-3 mb-3 rounded">
          ✓ Listo. Pegá la etiqueta y escaneala.
        </div>
      );
    }
    if (isFailed) {
      return (
        <div className="bg-red-100 border-l-4 border-red-500 p-3 mb-3 rounded">
          ✗ Falló — revisá impresora
        </div>
      );
    }
    if (isInProgress) {
      return (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 p-3 mb-3 rounded">
          🖨 Imprimiendo... ({printStatus})
        </div>
      );
    }
    return null;
  };
  // Pack: track which pack items have been confirmed (added to batch) in this session
  const [packItemsConfirmed, setPackItemsConfirmed] = useState<Set<string>>(new Set());
  const [addingPackItem, setAddingPackItem] = useState<string | null>(null);
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [codeInput, setCodeInput] = useState('');
  const knownCodes: string[] = (result as any).known_codes || [];

  // Load order items with reception status for partial delivery
  useEffect(() => {
    const mlOrderId = result.order?.ml_order_id;
    const sid = result.seller?.id || sellerId;
    if (!mlOrderId) return;
    setLoadingItems(true);
    api.get<any>(`/warehouse/order-items/${mlOrderId}?seller_id=${sid}`).then(res => {
      if (res.success && res.data) {
        setOrderItems((res.data as any).items || []);
        setFullyReceived((res.data as any).fully_received || false);
      }
    }).catch(() => {}).finally(() => setLoadingItems(false));
  }, [result.order?.ml_order_id, result.seller?.id, sellerId]);

  // Pre-fill close-scan if scanned ML label
  useEffect(() => {
    if ((result as any).scanned_ml_label && (result as any).prefill_close_code) {
      setCloseScanCode((result as any).prefill_close_code);
    }
    // Cada escaneo nuevo pliega el bloque de mensajes: el foco tiene que volver al escáner, no
    // quedarse en el cuadro de texto del chat del paquete anterior.
    setMessagesOpen(false);
    // Voltaje 110 V: el aviso es por paquete, se recalcula en cada escaneo.
    setVoltageMissing(!!result.voltage_label_missing);
    setVoltageBusy(false);
    // Auto-focus close-scan input on mount
    setTimeout(() => closeRef.current?.focus(), 200);
  }, [result]);

  const orderSellerId = result.seller?.id || sellerId;
  const targetBatch = batches.find(b => b.seller_id === orderSellerId);

  // ── Mensajes con el comprador (preferencia por usuario, voltaje 110V D6) ──
  // SOLO el número de PAQUETE (`orders.pack_id`). El número de venta NO sirve: cuando la venta
  // pertenece a un paquete, Mercado Libre contesta 400 "The order belong to a pack/purchase" y el
  // operario veía "Sin mensajes". Si de verdad no hay paquete, no se ofrece el bloque.
  const whPopupMessages = useAuthStore(s => s.user?.preferences?.wh_popup_messages === true);
  const messagesPackId = Number(result.order?.pack_id ?? result.pack?.pack_id ?? 0) || null;
  // El pop-up habla con las rutas del DEPÓSITO, que piden la misma llave que el escaneo
  // (`warehouse.operate`). Así el operario lee y contesta sin tener los permisos de la pantalla
  // de Mensajes, que no se reparten solos.
  const showBuyerMessages = whPopupMessages && canOperate;
  const messagesBasePath = '/warehouse/packs/{packId}/messages';

  // Order status flags
  const isCancelled = result.order?.status === 'cancelled' || result.shipment?.status === 'cancelled';
  const hasClaim = !!result.claim;
  // Retención por cupo de importación del comprador: venta viva pero despacho
  // congelado por ML (sin etiqueta). Ámbar: apartar el paquete, no despachar.
  const isQuotaHold = !isCancelled && !!(result as any).receiver_quota_hold;
  const isBlocked = isCancelled;
  const needsClaimConfirmation = hasClaim && !claimConfirmed && !isCancelled;

  // Confirmar un producto del pack (agregarlo al lote). Compartido entre el
  // botón manual "Confirmar" y la auto-confirmación al escanear un tracking
  // de Amazon.
  const confirmPackItem = async (pi: any) => {
    if (!targetBatch || addingPackItem) return;
    setAddingPackItem(pi.ml_order_id);
    try {
      const res: any = await api.post('/warehouse/items', {
        batch_id: targetBatch.id,
        seller_id: orderSellerId,
        ml_order_id: pi.ml_order_id,
        ml_shipment_id: pi.ml_shipment_id || result.shipment?.ml_shipment_id,
        order_id: pi.order_id,
        amazon_tracking: pi.amz_tracking,
        amazon_purchase_id: pi.amazon_purchase_id,
        asin: pi.asin || pi.seller_sku,
        title: pi.title,
        quantity: pi.quantity || 1,
        source: 'pack_scan',
        associated_codes: newCodes,
        ...(noBagReason ? { no_bag_reason: noBagReason } : {}),
        ...courierKeyBody,
      });
      if (res.success) {
        setPackItemsConfirmed(prev => new Set([...prev, pi.ml_order_id]));
      }
    } catch (err) { console.error('Add pack item failed', err); }
    finally { setAddingPackItem(null); }
  };

  // Auto-confirmar el producto del pack que corresponde al paquete recién
  // escaneado. SOLO cuando se escaneó un tracking de Amazon (paquete físico
  // en mano, result.purchase presente) — NUNCA al escanear la etiqueta ML del
  // pack, porque esa etiqueta no prueba que esta unidad haya llegado.
  const autoConfirmAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!result.pack?.items?.length) return;
    if ((result as any).scanned_ml_label) return;
    if (!result.purchase) return;
    if (!targetBatch || isBlocked || needsClaimConfirmation) return;
    const scanned = result.pack.items.find((pi: any) => pi.ml_order_id === result.order?.ml_order_id);
    if (!scanned) return;
    if (scanned.wh_item_id || packItemsConfirmed.has(scanned.ml_order_id)) return;
    const key = String(scanned.ml_order_id);
    if (autoConfirmAttempted.current.has(key)) return;
    autoConfirmAttempted.current.add(key);
    confirmPackItem(scanned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, targetBatch?.id, isBlocked, needsClaimConfirmation]);

  const typeLabels: Record<string, string> = {
    amazon_direct: 'Amazon Purchase', amazon_report: 'Amazon Report',
    stock_po: 'Stock PO', ml_shipment: 'ML Shipment',
    ml_order: 'ML Order', no_match: t('wh.noMatch'),
    internal_pick: 'Stock Interno',
  };
  const typeColors: Record<string, string> = {
    amazon_direct: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    amazon_report: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    stock_po: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    ml_shipment: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
    ml_order: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    no_match: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    internal_pick: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  };

  const getAddData = () => {
    if (result.type === 'amazon_direct' && result.purchase) {
      return {
        seller_id: orderSellerId, ml_order_id: result.order?.ml_order_id,
        ml_shipment_id: result.shipment?.ml_shipment_id, order_id: result.order?.order_id,
        amazon_tracking: result.purchase.tracking_number, amazon_purchase_id: result.purchase.id,
        asin: result.purchase.asin, title: result.items?.[0]?.title,
        quantity: result.purchase.quantity, source: 'amazon_scan',
      };
    }
    if (result.type === 'ml_shipment' || result.type === 'ml_order') {
      return {
        seller_id: orderSellerId, ml_order_id: result.order?.ml_order_id,
        ml_shipment_id: result.shipment?.ml_shipment_id, order_id: result.order?.order_id,
        asin: result.items?.[0]?.seller_sku?.substring(0, 10),
        title: result.items?.[0]?.title, source: 'ml_scan',
      };
    }
    // Stock interno: imprimir NO agrega al batch — el escaneo de la etiqueta ML
    // en este popup hace add-and-close en un paso, igual que el flujo Amazon.
    if (result.type === 'internal_pick') {
      return {
        seller_id: orderSellerId, ml_order_id: result.order?.ml_order_id,
        ml_shipment_id: result.shipment?.ml_shipment_id, order_id: result.order?.order_id,
        asin: result.internal_stock?.asin || result.internal_stock?.internal_sku,
        title: result.internal_stock?.product_title || result.items?.[0]?.title,
        quantity: result.internal_stock?.quantity, source: 'internal_pick',
      };
    }
    return null;
  };

  const addData = getAddData();
  const warnings: string[] = [];
  if (result.already_processed) warnings.push(t('wh.alreadyProcessed'));
  if (result.already_associated) warnings.push(t('wh.alreadyAssociated'));
  if (result.claim) warnings.push(`${t('wh.claimOpen')}: ${result.claim.reason || result.claim.type}`);
  if (result.cross_seller) warnings.push(`${t('wh.crossSeller')}: ${result.seller?.nickname}`);
  if (result.order?.status === 'cancelled') warnings.push(t('wh.orderCancelled'));
  if (isQuotaHold) warnings.push(t('wh.quotaHold'));

  const handleCloseScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!closeScanCode.trim() || !targetBatch) return;
    setCloseScanLoading(true);
    setCloseScanError('');
    try {
      // Pack flow: add all unconfirmed pack items first, then close all with pack-scan
      if (result.pack && result.pack.items?.length > 0) {
        // Step 0 (pre-validación): que la etiqueta de ML escaneada corresponda
        // a alguno de los envíos del pack. Si no matchea, NO agregamos nada al
        // lote — antes el operario terminaba con todas las unidades del pack
        // marcadas como recibidas pero sin cerrar.
        const shipmentIds = (result.pack.items as any[])
          .map((pi: any) => pi.ml_shipment_id)
          .filter(Boolean);
        if (result.shipment?.ml_shipment_id) shipmentIds.push(result.shipment.ml_shipment_id);
        const validation: any = await api.post('/warehouse/validate-close-code', {
          code: closeScanCode.trim(),
          ml_shipment_ids: Array.from(new Set(shipmentIds.map(String))),
          seller_id: orderSellerId,
        });
        if (!validation?.success || !validation.valid) {
          setCloseScanError(
            'Esta etiqueta de Mercado Libre no corresponde al pack. No se recibió nada — verificá la etiqueta y volvé a escanear.'
          );
          return;
        }
        // Step 1: Add any pack items not yet confirmed to the batch
        for (const pi of result.pack.items) {
          const alreadyConfirmed = pi.wh_item_id || packItemsConfirmed.has(pi.ml_order_id);
          if (!alreadyConfirmed) {
            try {
              await api.post('/warehouse/items', {
                batch_id: targetBatch.id,
                seller_id: orderSellerId,
                ml_order_id: pi.ml_order_id,
                ml_shipment_id: pi.ml_shipment_id || result.shipment?.ml_shipment_id,
                order_id: pi.order_id,
                amazon_tracking: pi.amz_tracking,
                amazon_purchase_id: pi.amazon_purchase_id,
                asin: pi.asin || pi.seller_sku,
                title: pi.title,
                quantity: pi.quantity || 1,
                source: 'pack_scan',
                associated_codes: newCodes,
                ...(noBagReason ? { no_bag_reason: noBagReason } : {}),
        ...courierKeyBody,
              });
            } catch (err) {
              console.warn('[pack] Failed to add pack item', pi.ml_order_id, err);
            }
          }
        }
        // Step 2: Close all pack items with the ML shipping label
        const res: any = await api.post('/warehouse/close-pack-scan', {
          code: closeScanCode.trim(),
          batch_id: targetBatch.id,
          seller_id: orderSellerId,
        });
        if (res.success && res.closed_count > 0) {
          onItemAdded();
        } else {
          setCloseScanError(res.error || t('wh.wrongLabel'));
        }
      } else {
        // Normal single-order flow
        if (!addData) return;
        const res: any = await api.post('/warehouse/add-and-close', {
          close_code: closeScanCode.trim(),
          batch_id: targetBatch.id,
          seller_id: addData.seller_id || orderSellerId,
          ml_order_id: addData.ml_order_id,
          ml_shipment_id: addData.ml_shipment_id,
          order_id: addData.order_id,
          amazon_tracking: addData.amazon_tracking,
          amazon_purchase_id: addData.amazon_purchase_id,
          asin: addData.asin,
          title: addData.title,
          quantity: addData.quantity || 1,
          source: addData.source || 'scan',
          associated_codes: newCodes,
          ...(noBagReason ? { no_bag_reason: noBagReason } : {}),
        ...courierKeyBody,
        });
        if (res.success && res.closed) {
          onItemAdded();
        } else {
          setCloseScanError(res.error || t('wh.wrongLabel'));
        }
      }
    } catch (err: any) {
      setCloseScanError(err.message || t('wh.wrongLabel'));
    } finally {
      setCloseScanLoading(false);
      setCloseScanCode('');
    }
  };

  const firstItem = result.items?.[0];
  // Para amazon_report el producto vive en shipment_items, no en items.
  const reportItem = result.shipment_items?.[0];
  const thumbnail = firstItem?.thumbnail || reportItem?.thumbnail || null;
  const asin =
    result.purchase?.asin ||
    firstItem?.seller_sku?.substring(0, 10) ||
    reportItem?.asin ||
    null;
  const resolvedAsin = asin;
  const title = firstItem?.title || reportItem?.title || result.purchase?.asin || '';
  const qty =
    result.purchase?.quantity || firstItem?.quantity || reportItem?.quantity || 1;
  const isPack = !!(result.pack && result.pack.items && result.pack.items.length > 0);

  const handleConvertLeftover = async (si: any) => {
    // El stock sobrante queda bajo el vendedor de las ventas del paquete.
    const targetSeller = result.matched_orders?.[0]?.seller?.id || orderSellerId;
    setLeftoverBusy(true);
    try {
      const res: any = await api.post('/warehouse/leftover/prepare-internal-stock', {
        amazon_order_id: si.amazon_order_id,
        asin: si.asin,
        seller_id: targetSeller,
      });
      if (!res.success) throw new Error(res.error || 'Error al preparar la unidad');
      setConvertModal({
        stockProductId: res.stock_product_id,
        asin: res.asin,
        title: res.product_title,
        unitCost: res.unit_cost,
        qtyOnHand: res.qty_on_hand,
      });
    } catch (err: any) {
      alert(err?.message || 'Error al preparar la unidad sobrante');
    } finally {
      setLeftoverBusy(false);
    }
  };

  // Rescate desde escaneo bloqueado (orden cancelada) o retenido sin cupo:
  // mismo circuito que el botón del acordeón de Orders. El backend auto-recibe
  // las unidades si nunca fueron ingresadas (source amazon_purchase) y asienta
  // la recuperación en el libro. Ojo sin-cupo: la venta sigue viva — si ML
  // libera el envío después, la unidad ya quedó en stock interno.
  const handleConvertCancelled = async (reason: 'cancelled' | 'quota_hold' | 'already_processed' = 'cancelled') => {
    const p = result.purchase;
    if (!p?.asin) { alert('Esta compra no tiene ASIN'); return; }
    setLeftoverBusy(true);
    try {
      const lookup: any = await api.get(
        `/internal-stock/product-by-asin?asin=${p.asin}&seller_id=${orderSellerId}&create=1&title=${encodeURIComponent(title || p.asin)}`
      );
      if (!lookup.success || !lookup.data) {
        throw new Error(lookup.error || 'No se pudo preparar la ficha de stock para este producto');
      }
      setConvertModal({
        stockProductId: lookup.data.product_id,
        asin: p.asin,
        title: lookup.data.title || title || p.asin,
        unitCost: p.unit_cost != null ? parseFloat(String(p.unit_cost)) : 0,
        qtyOnHand: lookup.data.qty_on_hand ?? 0,
        qty: Number(p.quantity) || 1,
        source: {
          type: 'amazon_purchase',
          purchase_id: p.id,
          ml_order_id: result.order?.ml_order_id ? String(result.order.ml_order_id) : undefined,
        },
        defaultNotes:
          reason === 'quota_hold'
            ? `Rescate de venta retenida sin cupo #${result.order?.ml_order_id ?? ''} (escaneo WH)`
            : reason === 'already_processed'
              ? `Paquete extra de compra ya procesada — venta #${result.order?.ml_order_id ?? ''} (escaneo WH)`
              : `Rescate de orden ML cancelada #${result.order?.ml_order_id ?? ''} (escaneo WH)`,
        sellerId: orderSellerId,
      });
    } catch (err: any) {
      alert(err?.message || 'Error al preparar el rescate');
    } finally {
      setLeftoverBusy(false);
    }
  };

  // Tarjeta de orden (estilo tab Orders) — compartida por "Órdenes Coincidentes"
  // (amazon_report) y "Otras ventas del mismo producto" (paquete ya procesado).
  const renderOrderCard = (mo: any, i: number) => {
    const moItems: any[] = mo.items || [];
            const moPrimary = moItems[0];
            const moQty = moItems.reduce((s: number, it: any) => s + Number(it.quantity || 0), 0) || 1;
            const moThumb = moPrimary?.thumbnail || null;
            return (
              <div key={i} className="bg-[var(--color-bg)] rounded-xl p-4 space-y-3 border border-[var(--color-border)]">
                {/* Fila principal: foto + título + estado */}
                <div className="flex gap-3">
                  {moThumb ? (
                    <img src={hiRes(moThumb) || ''} alt="" className="w-16 h-16 rounded-lg object-cover bg-[var(--color-surface)] shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-[var(--color-surface)] flex items-center justify-center shrink-0">
                      <Package size={24} className="text-[var(--color-text-muted)]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-medium text-[var(--color-text)] line-clamp-2 leading-tight">
                        {moPrimary?.title || mo.asin}
                      </span>
                      <StatusBadge status={mo.order.status} />
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 text-xs flex-wrap">
                      <EntityLink type="ml_order" value={mo.order.ml_order_id} sellerId={mo.seller?.id || sellerId} />
                      {moQty > 1 && (
                        <span className="text-[var(--color-error)] font-bold bg-[var(--color-error-light)] px-1.5 py-0.5 rounded text-[10px]">x{moQty}</span>
                      )}
                      {moPrimary?.unit_price != null && canMoney && (
                        <span className="text-[var(--color-text-secondary)] font-semibold">
                          {Number(moPrimary.unit_price).toLocaleString('en-US', { style: 'currency', currency: moPrimary.currency_id || 'USD' })}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-[var(--color-text-secondary)] flex-wrap">
                      <User size={12} className="shrink-0" />
                      {/* El nombre real del comprador exige la llave; el apodo se ve siempre. */}
                      <Can cap="global.view_buyers" fallback={<span>{mo.order.buyer_nickname || '--'}</span>}>
                        <span>{mo.order.buyer_name || mo.order.buyer_nickname || '--'}</span>
                        {mo.order.buyer_name && mo.order.buyer_nickname && (
                          <span className="text-[var(--color-text-muted)]">({mo.order.buyer_nickname})</span>
                        )}
                      </Can>
                      {mo.order.date_created && (
                        <>
                          <span>·</span>
                          <span>{new Date(mo.order.date_created).toLocaleDateString()}</span>
                        </>
                      )}
                      {mo.seller?.nickname && (
                        <>
                          <span>·</span>
                          <span className="font-medium">{mo.seller.nickname}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* Items adicionales (orden con más de un producto) */}
                {moItems.length > 1 && (
                  <div className="space-y-1.5">
                    {moItems.slice(1).map((it: any, j: number) => (
                      <div key={j} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-surface)]">
                        <img src={it.thumbnail || ''} alt="" className="w-8 h-8 rounded object-cover bg-[var(--color-bg)]" />
                        <span className="text-xs text-[var(--color-text)] flex-1 truncate">{it.title}</span>
                        <span className="text-xs text-[var(--color-text-muted)]">x{it.quantity}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Badges: envío / reclamo / cruces */}
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="font-mono text-[var(--color-text-muted)]">ASIN: {mo.asin}</span>
                  {mo.shipment && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
                      <Truck size={12} /> {mo.shipment.substatus || mo.shipment.status}
                    </span>
                  )}
                  {mo.claim && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                      <AlertTriangle size={12} /> {t('wh.claimOpen')}: {mo.claim.reason || mo.claim.type}
                    </span>
                  )}
                  {mo.cross_seller && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      {t('wh.crossSeller')}
                    </span>
                  )}
                  {mo.already_processed && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                      {t('wh.alreadyProcessed')}
                    </span>
                  )}
                </div>
                {/* Agregar al lote = re-escanear por número de orden para
                    reabrir el popup con la orden seleccionada y el flujo
                    completo (cierre con etiqueta ML, impresión, bolsa). */}
                <Can cap="warehouse.operate">
                  <button
                    onClick={() => onRescan?.(String(mo.order.ml_order_id))}
                    className="w-full px-4 py-3 bg-green-600 text-white text-base font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {t('wh.addToBatch')}
                  </button>
                </Can>
              </div>
            );
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className={`relative isolate overflow-hidden flex flex-col rounded-2xl shadow-2xl w-full max-w-5xl max-h-[96vh] ${
        isCancelled
          ? 'bg-red-50 dark:bg-red-950 ring-4 ring-red-500'
          : isQuotaHold
          ? 'bg-amber-50 dark:bg-amber-950 ring-4 ring-amber-500'
          : hasClaim
          ? 'bg-orange-50 dark:bg-orange-950 ring-4 ring-orange-500'
          : 'bg-[var(--color-bg-card)]'
      }`}
        onClick={e => e.stopPropagation()}>
        {/* 2026-09-10 — bandera del país de destino como marca de agua (muy suave, detrás de todo) */}
        <CountryFlagWatermark
          site={result.destination_site}
          enabled={result.flag_watermark !== false && !isCancelled}
        />

        {/* Header: compact (always visible) */}
        <div className={`shrink-0 flex items-center justify-between px-4 py-2.5 border-b ${
          isCancelled ? 'border-red-300 dark:border-red-700' : hasClaim ? 'border-orange-300 dark:border-orange-700' : 'border-[var(--color-border)]'
        }`}>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${typeColors[result.type] || ''}`}>
              {typeLabels[result.type] || result.type}
            </span>
            {result.seller && <span className="text-lg font-bold text-[var(--color-text)]">{result.seller.nickname}</span>}
            {result.order && <StatusBadge status={result.order.status} />}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--color-bg-hover)] rounded-lg">
            <X size={20} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>

        {/* 2026-09-10 — franja de correo + bolsa destino (depósito con dos correos) */}
        {result.order && !isCancelled && (
          <CourierBand
            courier={result.courier}
            courierUnknown={result.courier_unknown}
            targetBag={effectiveTargetBag}
            bagGap={effectiveBagGap}
            mode={result.mode}
            couriers={result.couriers ?? []}
            chosenCourierKey={chosenCourierKey}
            onChooseCourier={(key) => setChosenCourierKey(key)}
            onBagOpened={(bag) => setBagOverride(bag)}
          />
        )}

        {/* 2026-10-17 — número de orden Amazon de la venta + "Solicitar devolución en Amazon".
            Con la venta bloqueada (cancelada) el botón ya está en el pie: acá solo el número. */}
        <AmazonOrdersStrip orders={result.amazon_orders} showReturn={!isBlocked} />

        {/* Print state badge + reprint (Task 14) */}
        {(printJob || result.order_label_id) && (
          <div className="shrink-0 px-4 pt-2 flex items-center gap-2 flex-wrap">
            <div className="flex-1 min-w-0">{renderPrintBadge()}</div>
            {result.order_label_id && canOperate && (
              <button
                onClick={handleReprint}
                className="text-xs bg-orange-500 text-white px-2.5 py-1 rounded hover:bg-orange-600 shrink-0"
              >
                🖨 Reimprimir
              </button>
            )}
          </div>
        )}

        {/* Voltaje 110 V: el PDF salió sin la hoja del aviso (spec §5.5) */}
        {voltageMissing && (
          <div className="shrink-0 mx-4 mt-2 p-2.5 bg-red-100 dark:bg-red-900/40 border-2 border-red-500 rounded-xl">
            <div className="flex items-start gap-2.5">
              <span className="text-red-600 dark:text-red-400 shrink-0 text-lg leading-none">⚡</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-red-800 dark:text-red-300 leading-tight">
                  Falta la etiqueta 110 V de este paquete — reimprimir
                </p>
                <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                  El aviso "PRODUCTO DISEÑADO SOLO PARA 110V" no se pudo imprimir. Regeneralo y
                  pegalo antes de despachar.
                </p>
              </div>
              {canOperate && (
                <button
                  onClick={handleVoltageRelabel}
                  disabled={voltageBusy}
                  className="text-xs bg-red-600 text-white px-2.5 py-1 rounded hover:bg-red-700 disabled:opacity-60 shrink-0"
                >
                  {voltageBusy ? 'Regenerando…' : '🖨 Regenerar e imprimir'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* No-label warning: ML hasn't generated the label yet (handling or ready_to_ship without PDF) */}
        {!result.order_label_id && result.shipment && !['shipped','delivered','not_delivered','cancelled'].includes(result.shipment.status) && (() => {
          const substatusMessages: Record<string, string> = {
            validating_receiver_documentation: 'Mercado Libre está validando los datos del comprador. La etiqueta saldrá automáticamente cuando termine.',
            pending_receiver_address: 'Falta que el comprador confirme su dirección.',
            buffered: 'Mercado Libre todavía no asignó la etiqueta (estado intermedio).',
          };
          const statusMessages: Record<string, string> = {
            handling: 'Mercado Libre todavía está armando el envío. La etiqueta aún no está lista.',
            pending: 'Envío pendiente en Mercado Libre. La etiqueta aún no está lista.',
            to_be_agreed: 'Envío a coordinar con el comprador. No hay etiqueta automática.',
          };
          const sub = result.shipment.substatus as string | null | undefined;
          const st = result.shipment.status as string;
          const explanation = (sub && substatusMessages[sub]) || statusMessages[st] || null;
          return (
            <div className="shrink-0 mx-4 mt-2 p-2.5 bg-yellow-100 dark:bg-yellow-900/40 border-2 border-yellow-500 rounded-xl">
              <div className="flex items-start gap-2.5">
                <span className="text-yellow-600 dark:text-yellow-400 shrink-0 text-lg leading-none">⚠️</span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-yellow-800 dark:text-yellow-300 leading-tight">
                    NO despachar — Etiqueta de Mercado Libre aún no disponible
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                    {explanation
                      ? explanation
                      : `Envío en estado "${sub || st}". Mercado Libre todavía no generó la etiqueta para imprimir.`}
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1 font-semibold">
                    Apartá el paquete y volvé a escanearlo más tarde.
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Cancelled banner */}
        {isCancelled && (
          <div className="shrink-0 mx-4 mt-2 p-2.5 bg-red-100 dark:bg-red-900/40 border-2 border-red-500 rounded-xl animate-pulse">
            <div className="flex items-center gap-2.5">
              <XCircle size={22} className="text-red-600 dark:text-red-400 shrink-0" />
              <div>
                <p className="text-base font-bold text-red-800 dark:text-red-300 leading-tight">{t('wh.orderCancelled')}</p>
                <p className="text-xs text-red-600 dark:text-red-400">{t('wh.cannotProcess')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Claim banner */}
        {hasClaim && !isCancelled && (
          <div className="shrink-0 mx-4 mt-2 p-2.5 bg-orange-100 dark:bg-orange-900/40 border-2 border-orange-500 rounded-xl">
            <div className="flex items-center gap-2.5">
              <AlertTriangle size={22} className="text-orange-600 dark:text-orange-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-orange-800 dark:text-orange-300 leading-tight">{t('wh.claimOpen')}</p>
                <p className="text-xs text-orange-600 dark:text-orange-400 truncate">
                  {result.claim?.reason || result.claim?.type || 'Reclamo abierto'}
                </p>
              </div>
              {!claimConfirmed && (
                <Can cap="warehouse.ship_despite_claim">
                  <button
                    onClick={() => setClaimConfirmed(true)}
                    className="shrink-0 px-3 py-2 bg-orange-600 text-white text-sm font-bold rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    {t('wh.confirmShipmentWithClaim')}
                  </button>
                </Can>
              )}
              {claimConfirmed && (
                <span className="shrink-0 text-xs font-semibold text-orange-700 dark:text-orange-300 flex items-center gap-1">
                  <Check size={14} /> {t('wh.claimConfirmed')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Other warnings */}
        {warnings.filter(w => !w.includes(t('wh.orderCancelled')) && !w.includes(t('wh.claimOpen'))).length > 0 && (
          <div className="shrink-0 mx-4 mt-2 flex flex-wrap gap-1.5">
            {warnings.filter(w => !w.includes(t('wh.orderCancelled')) && !w.includes(t('wh.claimOpen'))).map((w, i) => (
              <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-600 rounded-lg">
                <AlertTriangle size={14} className="text-yellow-600 shrink-0" />
                <span className="text-xs font-medium text-yellow-800 dark:text-yellow-300">{w}</span>
              </div>
            ))}
          </div>
        )}

        {/* Body — scrollable area, all metadata grouped */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          {/* Info-only fallback: tracking encontrado en fuentes auxiliares pero NO accionable.
              Casos: orden cancelada, compra Amazon huérfana / cross-seller, tracking Push sin link.
              El operador VE el contexto y aparta el paquete físicamente. NO se agrega a batch. */}
          {result.type === 'info_only' && result.sub_type === 'amazon_purchase' && result.data && (
            <div className="py-6 px-2">
              <div className="text-center mb-4">
                <AlertTriangle size={56} className="mx-auto mb-3 text-orange-400" />
                <p className="text-xl text-[var(--color-text)] font-bold">
                  {result.data.order_status === 'cancelled'
                    ? 'ORDEN ML CANCELADA'
                    : result.data.ml_order_id
                      ? 'Compra Amazon (otro seller / grupo)'
                      : 'Compra Amazon huérfana (sin venta ML)'}
                </p>
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                  Tracking: <span className="font-mono font-bold">{result.code}</span>
                </p>
                <p className="text-base font-bold text-orange-600 dark:text-orange-400 mt-3">
                  Apartar paquete — NO despachar
                </p>
              </div>
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3 space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  {result.data.thumbnail && (
                    <img src={hiRes(result.data.thumbnail) || result.data.thumbnail} alt="" className="w-16 h-16 rounded object-contain bg-white shrink-0" />
                  )}
                  <div className="flex-1 min-w-0 space-y-1">
                    {result.data.product_title && (
                      <p className="font-medium text-[var(--color-text)] line-clamp-2">{result.data.product_title}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--color-text-secondary)]">
                      {result.data.asin && <EntityLink type="asin" value={result.data.asin} sellerId={sellerId} />}
                      {result.data.quantity != null && <span>Qty: <b>{result.data.quantity}</b></span>}
                      {result.data.amazon_status && <span>Status: <b>{result.data.amazon_status}</b></span>}
                      {result.data.carrier && <span>Courier: <b>{result.data.carrier}</b></span>}
                    </div>
                  </div>
                </div>
                <div className="border-t border-[var(--color-border)] pt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {result.data.seller_nickname && (
                    <div><span className="text-[var(--color-text-secondary)]">Seller:</span> <b>{result.data.seller_nickname}</b> ({result.data.seller_id})</div>
                  )}
                  {result.data.amazon_order_id && (
                    <div><span className="text-[var(--color-text-secondary)]">Amazon Order:</span> <span className="font-mono">{result.data.amazon_order_id}</span></div>
                  )}
                  {result.data.ml_order_id && (
                    <div><span className="text-[var(--color-text-secondary)]">ML Order:</span> <EntityLink type="ml_order" value={String(result.data.ml_order_id)} sellerId={result.data.seller_id} /></div>
                  )}
                  {result.data.order_status && (
                    <div><span className="text-[var(--color-text-secondary)]">Estado orden:</span> <b className={result.data.order_status === 'cancelled' ? 'text-red-500' : ''}>{result.data.order_status}</b></div>
                  )}
                  {result.data.buyer_nickname && (
                    <div><span className="text-[var(--color-text-secondary)]">Comprador:</span> <b>{result.data.buyer_nickname}</b></div>
                  )}
                  {result.data.order_date_created && (
                    <div><span className="text-[var(--color-text-secondary)]">Fecha orden:</span> {new Date(result.data.order_date_created).toLocaleDateString()}</div>
                  )}
                  {result.data.claim_reason && (
                    <div className="col-span-2 text-red-600 dark:text-red-400"><b>Reclamo:</b> {result.data.claim_reason} — {result.data.claim_detail}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {result.type === 'info_only' && result.sub_type === 'ml_shipment' && result.data && (
            <div className="py-6 px-2">
              <div className="text-center mb-4">
                <XCircle size={56} className="mx-auto mb-3 text-red-400" />
                <p className="text-xl text-[var(--color-text)] font-bold">
                  {result.data.order_status === 'cancelled' || result.data.status === 'cancelled'
                    ? 'ENVÍO ML CANCELADO'
                    : 'Envío ML (otro seller / grupo)'}
                </p>
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                  Tracking: <span className="font-mono font-bold">{result.code}</span>
                </p>
                <p className="text-base font-bold text-red-600 dark:text-red-400 mt-3">
                  Apartar paquete — NO despachar
                </p>
              </div>
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3 space-y-1 text-sm">
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {result.data.seller_nickname && (
                    <div><span className="text-[var(--color-text-secondary)]">Seller:</span> <b>{result.data.seller_nickname}</b> ({result.data.seller_id})</div>
                  )}
                  {result.data.ml_order_id && (
                    <div><span className="text-[var(--color-text-secondary)]">ML Order:</span> <EntityLink type="ml_order" value={String(result.data.ml_order_id)} sellerId={result.data.seller_id} /></div>
                  )}
                  {result.data.ml_shipment_id && (
                    <div><span className="text-[var(--color-text-secondary)]">Shipment:</span> <span className="font-mono">{result.data.ml_shipment_id}</span></div>
                  )}
                  {result.data.status && (
                    <div><span className="text-[var(--color-text-secondary)]">Estado envío:</span> <b className={result.data.status === 'cancelled' ? 'text-red-500' : ''}>{result.data.status}</b>{result.data.substatus && <span className="text-[var(--color-text-secondary)]"> · {result.data.substatus}</span>}</div>
                  )}
                  {result.data.order_status && (
                    <div><span className="text-[var(--color-text-secondary)]">Estado orden:</span> <b className={result.data.order_status === 'cancelled' ? 'text-red-500' : ''}>{result.data.order_status}</b></div>
                  )}
                  {result.data.buyer_nickname && (
                    <div><span className="text-[var(--color-text-secondary)]">Comprador:</span> <b>{result.data.buyer_nickname}</b></div>
                  )}
                  {result.data.order_date_created && (
                    <div><span className="text-[var(--color-text-secondary)]">Fecha orden:</span> {new Date(result.data.order_date_created).toLocaleDateString()}</div>
                  )}
                  {result.data.claim_reason && (
                    <div className="col-span-2 text-red-600 dark:text-red-400"><b>Reclamo:</b> {result.data.claim_reason} — {result.data.claim_detail}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {result.type === 'info_only' && result.sub_type === 'package_tracking' && result.data && (
            <div className="py-6 px-2">
              <div className="text-center mb-4">
                <Truck size={56} className="mx-auto mb-3 text-blue-400" />
                <p className="text-xl text-[var(--color-text)] font-bold">
                  Tracking conocido (sin orden vinculada)
                </p>
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                  {result.data.carrier_name || result.data.carrier_code || 'Courier'}: <span className="font-mono font-bold">{result.code}</span>
                </p>
                <p className="text-base font-bold text-blue-600 dark:text-blue-400 mt-3">
                  Verificar manualmente — paquete sin venta asociada
                </p>
              </div>
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3 space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {result.data.seller_nickname && (
                    <div><span className="text-[var(--color-text-secondary)]">Seller:</span> <b>{result.data.seller_nickname}</b></div>
                  )}
                  {result.data.status && (
                    <div><span className="text-[var(--color-text-secondary)]">Estado:</span> <b>{result.data.status}</b>{result.data.status_detail && <span className="text-[var(--color-text-secondary)]"> · {result.data.status_detail}</span>}</div>
                  )}
                  {result.data.current_location && (
                    <div className="col-span-2"><span className="text-[var(--color-text-secondary)]">Ubicación:</span> {result.data.current_location}</div>
                  )}
                  {result.data.estimated_delivery && (
                    <div><span className="text-[var(--color-text-secondary)]">ETA:</span> {new Date(result.data.estimated_delivery).toLocaleDateString()}</div>
                  )}
                  {result.data.actual_delivery && (
                    <div><span className="text-[var(--color-text-secondary)]">Entregado:</span> {new Date(result.data.actual_delivery).toLocaleDateString()}</div>
                  )}
                </div>
                {Array.isArray(result.data.events) && result.data.events.length > 0 && (
                  <div className="border-t border-[var(--color-border)] pt-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] mb-1">Últimos eventos</p>
                    <ul className="space-y-1 text-xs">
                      {result.data.events.slice(0, 5).map((ev: any, idx: number) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-[var(--color-text-secondary)] shrink-0 font-mono">
                            {ev.event_time ? new Date(ev.event_time).toLocaleString('es', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                          <span className="text-[var(--color-text)]">
                            {ev.status && <b className="mr-1">{ev.status}</b>}
                            {ev.description}
                            {ev.location && <span className="text-[var(--color-text-secondary)]"> · {ev.location}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* No match */}
          {result.type === 'no_match' && (
            <div className="text-center py-10">
              <AlertTriangle size={56} className="mx-auto mb-4 text-red-400" />
              <p className="text-xl text-[var(--color-text)] font-bold">{t('wh.noMatchTitle')}</p>
              <p className="text-base text-[var(--color-text-secondary)] mt-2">{t('wh.noMatchDesc')}: <span className="font-mono font-bold">{result.code}</span></p>
              {onRequestLinkTracking && canOperate && (
                <button
                  onClick={() => onRequestLinkTracking(result.code || '')}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[var(--color-primary)] text-white font-bold hover:opacity-90"
                >
                  <Truck size={18} />
                  Vincular tracking manualmente
                </button>
              )}
            </div>
          )}

          {/* Pack-scanned context strip — only in pack mode */}
          {isPack && result.type !== 'no_match' && result.type !== 'info_only' && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)]">
              <span className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider shrink-0">Escaneaste:</span>
              {thumbnail && (
                <img src={hiRes(thumbnail) || thumbnail} alt="" className="w-8 h-8 rounded object-contain bg-white shrink-0" />
              )}
              <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap text-xs">
                {asin && <EntityLink type="asin" value={asin} sellerId={sellerId} />}
                {result.purchase?.tracking_number && (
                  <>
                    <span className="text-[var(--color-text-secondary)]">·</span>
                    <EntityLink type="tracking" value={result.purchase.tracking_number} sellerId={sellerId} />
                  </>
                )}
                {result.purchase?.amazon_status && (
                  <StatusBadge status={result.purchase.amazon_status} />
                )}
                {result.order && (
                  <>
                    <span className="text-[var(--color-text-secondary)]">·</span>
                    <EntityLink type="ml_order" value={result.order.ml_order_id} sellerId={sellerId} />
                    <span className="text-[var(--color-text-secondary)] truncate">{result.order.buyer_nickname}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Product card — hidden in pack mode (misleading first-product display) */}
          {!isPack && result.type !== 'no_match' && result.type !== 'info_only' && (
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="relative shrink-0">
                {thumbnail ? (
                  <img src={hiRes(thumbnail) || thumbnail} alt="" className="w-[88px] h-[88px] sm:w-[110px] sm:h-[110px] rounded-xl object-contain bg-white shadow-lg" />
                ) : (
                  <div className="w-[88px] h-[88px] sm:w-[110px] sm:h-[110px] bg-gray-200 dark:bg-gray-700 rounded-xl flex items-center justify-center shadow-lg">
                    <Package size={40} className="text-gray-400" />
                  </div>
                )}
                {qty > 1 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-base font-bold px-2 py-0.5 rounded-full shadow">
                    x{qty}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                {asin && (
                  <EntityLink type="asin" value={asin} sellerId={sellerId} className="text-base" />
                )}
                <p className="text-base sm:text-lg text-[var(--color-text)] leading-snug font-semibold line-clamp-3">{title}</p>
                {result.purchase?.product_title && result.purchase.product_title !== title && (
                  <p className="text-sm text-blue-600 dark:text-blue-400 leading-snug italic line-clamp-2">
                    AMZ: {result.purchase.product_title}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ML Sale + Amazon Purchase: side-by-side on md+ to halve vertical height. Hidden in pack mode (shown compact above). */}
          {!isPack && (result.order || result.purchase || result.internal_stock) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* ML Sale section */}
              {result.order && (
                <div className="p-3 rounded-xl border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20 space-y-1.5">
                  <div className="text-[10px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider">{t('wh.mlSale')}</div>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-[var(--color-text-secondary)]">Order:</span>
                    <EntityLink type="ml_order" value={result.order.ml_order_id} sellerId={sellerId} />
                    <StatusBadge status={result.order.status} />
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] flex-wrap">
                    <User size={14} className="shrink-0" />
                    <span className="font-medium truncate">{result.order.buyer_nickname}</span>
                    <span>·</span>
                    <span>{new Date(result.order.date_created).toLocaleDateString()}</span>
                  </div>
                  {result.shipment && (
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="text-[var(--color-text-secondary)]">Ship:</span>
                      <EntityLink type="ml_shipment" value={result.shipment.ml_shipment_id} sellerId={sellerId} />
                      <StatusBadge status={result.shipment.status} />
                    </div>
                  )}
                </div>
              )}

              {/* Amazon Purchase section */}
              {result.purchase && (
                <div className="p-3 rounded-xl border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 space-y-1.5">
                  <div className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">{t('wh.amazonPurchase')}</div>
                  <div className="flex items-center gap-3 text-sm flex-wrap">
                    {result.purchase.asin && (
                      <span className="flex items-center gap-1">
                        <span className="text-[var(--color-text-secondary)]">ASIN:</span>
                        <EntityLink type="asin" value={result.purchase.asin} sellerId={sellerId} />
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <StatusBadge status={result.purchase.amazon_status} />
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm flex-wrap">
                    {result.purchase.tracking_number && (
                      <span className="flex items-center gap-1">
                        <span className="text-[var(--color-text-secondary)]">Trk:</span>
                        <EntityLink type="tracking" value={result.purchase.tracking_number} sellerId={sellerId} />
                      </span>
                    )}
                    {result.purchase.created_at && (() => {
                      const daysAgo = Math.floor((Date.now() - new Date(result.purchase.created_at).getTime()) / 86400000);
                      return (
                        <span className="flex items-center gap-1">
                          <span className="text-blue-900 dark:text-blue-100 font-medium">{new Date(result.purchase.created_at).toLocaleDateString()}</span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            daysAgo > 7 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            : daysAgo > 3 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          }`}>
                            {daysAgo}d
                          </span>
                        </span>
                      );
                    })()}
                    {result.purchase.unit_cost && canMoney && (
                      <span className="flex items-center gap-1">
                        <span className="text-[var(--color-text-secondary)]">{t('wh.cost')}:</span>
                        <span className="text-blue-900 dark:text-blue-100 font-semibold">
                          ${Number(result.purchase.unit_cost).toFixed(2)}
                          {result.purchase.quantity > 1 && (
                            <> × {result.purchase.quantity} = ${(Number(result.purchase.unit_cost) * result.purchase.quantity).toFixed(2)}</>
                          )}
                        </span>
                      </span>
                    )}
                    {result.purchase.carrier && (
                      <span className="flex items-center gap-1">
                        <span className="text-blue-900 dark:text-blue-100">{result.purchase.carrier}</span>
                      </span>
                    )}
                  </div>
                </div>
              )}
              {/* Internal Stock section — reemplaza Amazon Purchase en internal picks */}
              {result.internal_stock && (
                <div className="p-3 rounded-xl border-l-4 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 space-y-1.5">
                  <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Stock Interno</div>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-[var(--color-text-secondary)]">Código interno:</span>
                    <span className="font-mono font-semibold text-emerald-900 dark:text-emerald-100">
                      {result.internal_stock.internal_sku || result.internal_stock.asin || '—'}
                    </span>
                  </div>
                  {result.internal_stock.product_title && (
                    <div className="flex items-start gap-2 text-sm flex-wrap">
                      <span className="text-[var(--color-text-secondary)] shrink-0">Producto:</span>
                      <span className="text-emerald-900 dark:text-emerald-100 line-clamp-2">{result.internal_stock.product_title}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-[var(--color-text-secondary)]">Sacar:</span>
                    <span className="font-bold text-emerald-900 dark:text-emerald-100">
                      {result.internal_stock.quantity} {result.internal_stock.quantity === 1 ? 'unidad' : 'unidades'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mensajes con el comprador (2026-09-22, voltaje 110V D6) — bloque plegable, debajo
              de los datos de la venta. Aparece SOLO si el usuario prendió la preferencia en
              Configuración › Depósitos; con la preferencia apagada el chat ni se monta, así que
              no se pide nada al servidor. Empieza plegado: el primer pedido sale cuando el
              operario lo abre. */}
          {showBuyerMessages && messagesPackId && (
            <div className="rounded-xl border border-blue-300 dark:border-blue-700 bg-blue-50/40 dark:bg-blue-900/10">
              <button
                type="button"
                onClick={() => setMessagesOpen(o => !o)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
              >
                {messagesOpen
                  ? <ChevronDown size={16} className="shrink-0 text-blue-600 dark:text-blue-400" />
                  : <ChevronRight size={16} className="shrink-0 text-blue-600 dark:text-blue-400" />}
                <MessageCircle size={16} className="shrink-0 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                  {t('wh.buyerMessages')}
                </span>
                <span className="ml-auto text-xs text-[var(--color-text-secondary)]">
                  {messagesOpen ? t('wh.buyerMessagesHide') : t('wh.buyerMessagesShow')}
                </span>
              </button>
              {messagesOpen && (
                <div className="px-3 pb-3">
                  <MessagePanel
                    embedded
                    basePath={messagesBasePath}
                    sendCap="warehouse.operate"
                    packId={messagesPackId}
                    buyerName={result.order?.buyer_nickname || ''}
                    sellerId={orderSellerId}
                    onClose={() => setMessagesOpen(false)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Product Codes — scan/type FNSKU, UPC, etc. */}
          {resolvedAsin && !isBlocked && result.type !== 'internal_pick' && (
            <div className="p-3 rounded-xl border border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-900/10 space-y-2">
              <div className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                Códigos del producto
              </div>
              {/* Known codes (from previous scans) */}
              {knownCodes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {knownCodes.map((c: string, i: number) => (
                    <span key={`known-${i}`} className="px-2.5 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-mono rounded-lg">
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {/* New codes added this session */}
              {newCodes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {newCodes.map((c: string, i: number) => (
                    <span key={`new-${i}`} className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-mono rounded-lg flex items-center gap-1.5">
                      {c}
                      <button onClick={() => setNewCodes(prev => prev.filter((_, idx) => idx !== i))}
                        className="text-blue-400 hover:text-red-500 transition-colors">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {/* Input for new code */}
              {canOperate && <input
                type="text"
                value={codeInput}
                onChange={e => setCodeInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = codeInput.trim();
                    if (val && !knownCodes.includes(val) && !newCodes.includes(val)) {
                      setNewCodes(prev => [...prev, val]);
                    }
                    setCodeInput('');
                  }
                }}
                placeholder="Escanear o escribir código..."
                className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm font-mono text-[var(--color-text)] outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30"
              />}
            </div>
          )}

          {/* Pack Products — responsive grid, 1/2/3 cols by viewport */}
          {result.pack && result.pack.items?.length > 0 && (
            <div className="rounded-xl border-2 border-indigo-300 dark:border-indigo-600 overflow-hidden">
              <div className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 border-b border-indigo-200 dark:border-indigo-700 flex items-center justify-between">
                <span className="text-xs sm:text-sm font-bold text-indigo-800 dark:text-indigo-300 flex items-center gap-1.5">
                  <Package size={14} />
                  Pack — {result.pack.total_items} productos
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  result.pack.items.every((pi: any) => pi.wh_item_id || packItemsConfirmed.has(pi.ml_order_id))
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                }`}>
                  {result.pack.items.filter((pi: any) => pi.wh_item_id || packItemsConfirmed.has(pi.ml_order_id)).length}/{result.pack.total_items} confirmados
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-2 bg-[var(--color-bg)]/30">
                {result.pack.items.map((pi: any, i: number) => {
                  const isConfirmed = !!pi.wh_item_id || packItemsConfirmed.has(pi.ml_order_id);
                  const isCurrentOrder = pi.ml_order_id === result.order?.ml_order_id;
                  const isAddingThis = addingPackItem === pi.ml_order_id;
                  const statusColors: Record<string, string> = {
                    ordered: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
                    shipped: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                    delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
                  };
                  return (
                    <div key={i} className={`relative flex flex-col gap-2 p-2 rounded-lg border ${
                      isConfirmed
                        ? 'border-green-300 dark:border-green-700 bg-green-50/60 dark:bg-green-900/15'
                        : isCurrentOrder
                        ? 'border-blue-400 dark:border-blue-600 bg-blue-50/50 dark:bg-blue-900/15 ring-1 ring-blue-300'
                        : 'border-[var(--color-border)] bg-[var(--color-bg-card)]'
                    }`}>
                      {/* Top row: thumb + status icon overlay + info */}
                      <div className="flex items-start gap-2">
                        <div className="relative shrink-0">
                          {pi.thumbnail ? (
                            <img src={pi.thumbnail.replace('http://', 'https://')} alt="" className="w-12 h-12 rounded-md object-contain bg-white shadow-sm" />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-md flex items-center justify-center">
                              <Package size={18} className="text-gray-400" />
                            </div>
                          )}
                          <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center shadow ${
                            isConfirmed ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                          }`}>
                            {isConfirmed
                              ? <Check size={12} className="text-white" />
                              : <Clock size={12} className="text-white" />}
                          </div>
                          {pi.quantity > 1 && (
                            <span className="absolute -bottom-1 -right-1 bg-red-500 text-white px-1 py-0 rounded-full text-[9px] font-bold leading-none">
                              x{pi.quantity}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-[var(--color-text)] leading-tight line-clamp-2">{pi.title}</p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {pi.asin && <span className="font-mono text-[10px] text-teal-600 dark:text-teal-400">{pi.asin}</span>}
                            {pi.amazon_status && (
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${statusColors[pi.amazon_status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                                {pi.amazon_status}
                              </span>
                            )}
                            {isCurrentOrder && <span className="text-[9px] text-blue-600 dark:text-blue-400 font-bold">●escaneado</span>}
                          </div>
                        </div>
                      </div>
                      {/* Bottom: action / status */}
                      {isConfirmed ? (
                        <div className="text-[10px] text-green-700 dark:text-green-400 font-bold text-center py-1 bg-green-100 dark:bg-green-900/30 rounded">
                          ✓ Confirmado
                        </div>
                      ) : targetBatch && !isBlocked && canOperate ? (
                        <button
                          disabled={isAddingThis}
                          onClick={() => confirmPackItem(pi)}
                          className="w-full px-2 py-1.5 bg-indigo-600 text-white text-[11px] font-bold rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {isAddingThis ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                          Confirmar
                        </button>
                      ) : (
                        <div className="text-[10px] text-gray-400 text-center py-1">Pendiente</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Order Items — compact partial delivery (only show if >1 item and NO pack) */}
          {!result.pack && orderItems.length > 1 && (
            <div className="mt-3 rounded-xl border border-[var(--color-border)] overflow-hidden">
              <div className="px-4 py-2 bg-[var(--color-bg)] border-b border-[var(--color-border)] flex items-center justify-between">
                <span className="text-sm font-bold text-[var(--color-text)]">
                  {t('wh.orderProducts')} ({orderItems.length})
                </span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  fullyReceived
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                }`}>
                  {orderItems.filter((i: any) => i.reception_status !== 'pending').length}/{orderItems.length} {t('wh.received')}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 p-3">
                {orderItems.map((oi: any, i: number) => {
                  const isReceived = oi.reception_status !== 'pending';
                  return (
                    <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                      isReceived
                        ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700'
                        : 'border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-600'
                    }`}>
                      {isReceived ? <Check size={16} className="text-green-500 shrink-0" /> : <Clock size={16} className="text-gray-400 shrink-0" />}
                      {oi.thumbnail && (
                        <img src={oi.thumbnail.replace('http://', 'https://')} alt="" className="w-10 h-10 rounded object-contain bg-white shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[var(--color-text)] leading-tight truncate max-w-[200px]">{oi.ml_title || oi.title}</p>
                        <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
                          {oi.asin && <span className="font-mono text-teal-600 dark:text-teal-400">{oi.asin}</span>}
                          {oi.quantity > 1 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[10px] font-bold">x{oi.quantity}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Matched orders (amazon_report) — card estilo tab Orders */}
          {result.matched_orders && result.matched_orders.length > 0 && (
            <div className="space-y-3">
              <span className="text-sm font-semibold text-[var(--color-text)]">{t('wh.matchedOrders')}</span>
              {result.matched_orders.map(renderOrderCard)}
            </div>
          )}

          {/* Paquete YA procesado: otras ventas del mismo producto sin lote
              (más vieja arriba). Misma tarjeta; el botón re-escanea por nº de orden. */}
          {result.already_processed && result.pending_same_product && result.pending_same_product.length > 0 && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 text-sm text-amber-900 dark:text-amber-200">
                {t('wh.pendingSameProductHint', { n: result.pending_same_product.length })}
              </div>
              <span className="text-sm font-semibold text-[var(--color-text)]">{t('wh.pendingSameProduct')}</span>
              {result.pending_same_product.map(renderOrderCard)}
            </div>
          )}
          {result.already_processed && result.type === 'amazon_direct' && result.pending_same_product && result.pending_same_product.length === 0 && (
            <div className="p-3 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-sm text-[var(--color-text-secondary)]">
              {t('wh.pendingSameProductNone')}
            </div>
          )}
          {/* Paquete ya procesado: alternativa a asignarlo a otra venta →
              rescatarlo como stock interno (mismo flujo que la orden cancelada). */}
          {result.already_processed && result.type === 'amazon_direct' && result.purchase?.asin && (
            <div className="flex justify-center">
              {(result.purchase as any).converted_to_stock ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <ArrowDownUp size={14} />
                  {t('wh.alreadyConverted')}
                </span>
              ) : (
                <Can cap="warehouse.convert_internal">
                  <button
                    onClick={() => handleConvertCancelled('already_processed')}
                    disabled={leftoverBusy}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-sm font-bold disabled:opacity-50"
                    title="Guardar las unidades de este paquete como stock interno vendible"
                  >
                    {leftoverBusy ? <Loader2 size={16} className="animate-spin" /> : <ArrowDownUp size={16} />}
                    {t('wh.convertToInternal')}
                  </button>
                </Can>
              )}
            </div>
          )}

          {/* Productos del paquete (amazon_report) — una tarjeta por producto.
              2026-08-29: antes solo el primero tenía foto/título y el resto era una
              fila anónima "unidad sobrante". Ahora cada uno muestra foto, título,
              cantidad, orden Amazon, tipo de compra (a mano / automática) y, si no
              tiene venta pendiente, "Elegir venta" (reasignar la compra) / convertir / saltear. */}
          {result.type === 'amazon_report' && (result.shipment_items || []).length > 0 && (
            <div className="space-y-3">
              <span className="text-sm font-semibold text-[var(--color-text)]">
                Productos en este paquete ({(result.shipment_items || []).length})
              </span>
              {(result.shipment_items || []).map((si: any, siIdx: number) => {
                const leftover = Number(si.leftover_qty || 0);
                const qtyBought = Number(si.quantity || 0);
                const sold = qtyBought - leftover;
                const matchedIds: string[] = (si.matched_ml_order_ids || []).map(String);
                const purchase = si.purchase || null;
                const srcLabel = purchase
                  ? purchase.link_source === 'manual'
                    ? 'Compra a mano'
                    : purchase.link_source === 'api'
                      ? 'Compra automática'
                      : `Compra (${purchase.link_source || 'registrada'})`
                  : 'Sin compra registrada en el sistema';
                return (
                  <div
                    key={`si-${siIdx}`}
                    className={`rounded-xl border-2 p-4 space-y-3 ${
                      leftover > 0
                        ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
                        : 'border-[var(--color-border)] bg-[var(--color-bg)]'
                    }`}
                  >
                    <div className="flex gap-3">
                      {si.thumbnail ? (
                        <img src={hiRes(si.thumbnail) || ''} alt="" className="w-16 h-16 rounded-lg object-cover bg-[var(--color-surface)] shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-[var(--color-surface)] flex items-center justify-center shrink-0">
                          <Package size={24} className="text-[var(--color-text-muted)]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <p className="text-sm font-semibold text-[var(--color-text)] line-clamp-2">{si.title || si.asin}</p>
                        <p className="text-xs text-[var(--color-text-secondary)]">
                          {si.asin} · {qtyBought} comprada(s) · orden Amazon {si.amazon_order_id || '—'}
                        </p>
                        <p className="text-xs text-[var(--color-text-secondary)]">
                          {srcLabel}
                          {purchase?.ml_order_id ? ` · enlazada a la venta ${purchase.ml_order_id}` : ''}
                          {purchase?.tracking_number ? ` · seguimiento ${purchase.tracking_number}` : ''}
                        </p>
                        {matchedIds.length > 0 && (
                          <p className="text-xs text-emerald-700 dark:text-emerald-300">
                            Venta pendiente: {matchedIds.join(', ')} (etiqueta arriba)
                          </p>
                        )}
                      </div>
                    </div>
                    {leftover > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                            {leftover} unidad(es) sin venta pendiente
                          </span>
                          <span className="text-xs text-[var(--color-text-secondary)]">
                            · {sold} con venta
                          </span>
                        </div>
                        {Array.from({ length: leftover }).map((_, unitIdx) => (
                          <div
                            key={unitIdx}
                            className="flex flex-wrap gap-2 items-center bg-[var(--color-bg)] rounded-lg p-3"
                          >
                            <span className="text-sm font-medium text-[var(--color-text)]">
                              Unidad {unitIdx + 1}
                            </span>
                            <Can cap="warehouse.operate">
                              <button
                                disabled={leftoverBusy}
                                onClick={() => setPendingSaleFor({ asin: si.asin, amazonOrderId: si.amazon_order_id, title: si.title })}
                                className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                              >
                                Elegir venta
                              </button>
                            </Can>
                            <Can cap="warehouse.convert_internal">
                              <button
                                disabled={leftoverBusy}
                                onClick={() => handleConvertLeftover(si)}
                                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                              >
                                Convertir a stock interno
                              </button>
                            </Can>
                            <button
                              disabled={leftoverBusy}
                              onClick={onClose}
                              className="px-3 py-2 bg-gray-400 text-white text-sm rounded-lg hover:bg-gray-500 disabled:opacity-50"
                            >
                              Saltear
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — always visible (shrink-0) so close-scan stays in view */}
        {isBlocked ? (
          <div className="shrink-0 p-4 border-t-2 border-red-500 bg-red-100 dark:bg-red-900/30 text-center">
            <p className="text-base font-bold text-red-700 dark:text-red-300">{t('wh.blockedCancelled')}</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {/* Criterio único: orden cancelada + compra Amazon presente = mismas
                  acciones, sin importar qué se escaneó (tracking Amazon, etiqueta
                  ML u orden ML — el backend adjunta la compra en los 3 casos). */}
              {result.purchase?.asin && result.order?.status === 'cancelled' && (
                (result.purchase as any).converted_to_stock ? (
                  <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    <ArrowDownUp size={14} />
                    {t('wh.alreadyConverted')}
                  </span>
                ) : (
                  <Can cap="warehouse.convert_internal">
                  <button
                    onClick={() => handleConvertCancelled('cancelled')}
                    disabled={leftoverBusy}
                    className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-sm font-bold disabled:opacity-50"
                    title="Rescatar las unidades de esta compra como stock interno vendible"
                  >
                    {leftoverBusy ? <Loader2 size={16} className="animate-spin" /> : <ArrowDownUp size={16} />}
                    {t('wh.convertToInternal')}
                  </button>
                  </Can>
                )
              )}
              {result.purchase?.amazon_order_id && can('warehouse.request_amazon_cancel') && (
                <a
                  href={`https://www.amazon.com/gp/your-account/order-details?orderID=${result.purchase.amazon_order_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-bold"
                  title="Abre la compra en la web de Amazon para pedir la cancelación o devolución (la API no permite cancelar)"
                >
                  <Truck size={16} />
                  {t('wh.requestAmazonCancel')}
                </a>
              )}
            </div>
          </div>
        ) : isQuotaHold ? (
          <div className="shrink-0 p-4 border-t-2 border-amber-500 bg-amber-100 dark:bg-amber-900/30 text-center">
            <p className="text-base font-bold text-amber-700 dark:text-amber-300">{t('wh.quotaHoldTitle')}</p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{t('wh.quotaHoldMsg')}</p>
            {/* Rescate opcional: apartar la mercadería como stock interno sin
                esperar a que el comprador resuelva su cupo. La venta sigue viva
                en ML — si después se libera, la unidad ya está en stock. */}
            {result.purchase?.asin && (
              (result.purchase as any).converted_to_stock ? (
                <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <ArrowDownUp size={14} />
                  {t('wh.alreadyConverted')}
                </span>
              ) : (
                <Can cap="warehouse.convert_internal">
                <button
                  onClick={() => handleConvertCancelled('quota_hold')}
                  disabled={leftoverBusy}
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-sm font-bold disabled:opacity-50"
                  title="Convertir las unidades de esta compra en stock interno vendible (la venta ML sigue retenida)"
                >
                  {leftoverBusy ? <Loader2 size={16} className="animate-spin" /> : <ArrowDownUp size={16} />}
                  {t('wh.convertToInternal')}
                </button>
                </Can>
              )
            )}
          </div>
        ) : needsClaimConfirmation ? (
          /* Claim needs confirmation first — no input shown */
          null
        ) : canOperate && (addData || result.pack) && targetBatch && result.type !== 'no_match' && result.type !== 'info_only' && (
          !result.already_processed ||
          (result.pack && result.pack.items?.some((pi: any) => !pi.wh_status || pi.wh_status !== 'closed'))
        ) ? (
          /* Close-scan input — always visible, add+close in 1 step (or pack close) */
          <div className="shrink-0 p-3 sm:p-4 border-t-2 border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10">
            <p className="text-xs sm:text-sm font-bold text-green-700 dark:text-green-300 mb-2 uppercase tracking-wider">
              {t('wh.scanToClose')}
            </p>
            <form onSubmit={handleCloseScan} className="flex items-center gap-2">
              <input
                ref={closeRef}
                type="text"
                value={closeScanCode}
                onChange={e => setCloseScanCode(e.target.value)}
                placeholder={t('wh.scanTracking')}
                className="flex-1 min-w-0 px-3 sm:px-4 py-3 bg-[var(--color-bg)] border-2 border-[var(--color-border)] rounded-xl text-base sm:text-lg font-mono text-[var(--color-text)] outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
                autoFocus
              />
              <button type="submit" disabled={closeScanLoading || !closeScanCode.trim()}
                className="shrink-0 px-4 sm:px-5 py-3 bg-green-600 text-white rounded-xl text-sm sm:text-base font-bold disabled:opacity-50 flex items-center gap-2"
              >
                {closeScanLoading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                <span className="hidden sm:inline">{t('wh.addAndClose')}</span>
              </button>
            </form>
            {closeScanError && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1.5">
                <AlertTriangle size={12} />{closeScanError}
              </p>
            )}
          </div>
        ) : null}
      </div>

      {convertModal && (
        <ConvertToInternalModal
          isOpen={true}
          onClose={() => {
            // La unidad ya fue recibida como stock por prepare-internal-stock.
            // Cerramos el popup de escaneo para forzar un re-escaneo con el
            // conteo de sobrantes actualizado y evitar recibir la misma unidad
            // dos veces.
            setConvertModal(null);
            onClose();
          }}
          onSuccess={() => {
            setConvertModal(null);
            onClose();
          }}
          sellerId={convertModal.sellerId ?? sellerId}
          stockProductId={convertModal.stockProductId}
          productTitle={convertModal.title}
          productAsin={convertModal.asin}
          qtyOnHand={convertModal.qtyOnHand}
          defaultQty={convertModal.qty ?? 1}
          lockQty={true}
          defaultUnitCost={canMoney ? convertModal.unitCost : 0}
          source={(convertModal.source ?? { type: 'manual_inventory' }) as any}
          defaultNotes={convertModal.defaultNotes}
        />
      )}
      {pendingSaleFor && pendingSaleFor.amazonOrderId && (
        <PendingSalesModal
          asin={pendingSaleFor.asin}
          amazonOrderId={pendingSaleFor.amazonOrderId}
          productTitle={pendingSaleFor.title}
          crossAll={true}
          onClose={() => setPendingSaleFor(null)}
          onReassigned={(mlOrderId) => {
            setPendingSaleFor(null);
            // El número de orden ML va a la barra del escáner: se abre el popup
            // clásico de esa venta (etiqueta, cierre, bolsa) como un escaneo normal.
            onRescan?.(mlOrderId);
          }}
        />
      )}
      {searchModalAsin && (
        <LeftoverSearchModal
          asin={searchModalAsin}
          onClose={() => setSearchModalAsin(null)}
          onAssociated={() => {
            setSearchModalAsin(null);
            onClose();
          }}
        />
      )}
    </div>
  );
}
